import React, {Fragment, useContext, useEffect, useState, useCallback, useRef, useLayoutEffect} from 'react';
import {Link} from "react-router";
import {Popover, Tag} from "@blueprintjs/core";

import {CreateLocation} from "./create"
import {SearchableDataGrid} from "../../../components/tables/data-grid";
import {locations, organisations} from "../../../services/api";
import {errorToast, successToast} from "../../../utils/toaster";
import {timeAgo} from "../../../utils/time";
import {DataPageContext} from "../context";
import {formatTag} from "../../../constants/tags";


// -------------------------------------------------
// ----------------------------------- auto-fit tags

const AutoFitTagsCell = ({tags}) => {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const fitRef = useRef(tags.length);
    const [, forceUpdate] = useState(0);

    const compute = useCallback(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure || !tags.length) return;

        const cell = container.closest('[role="gridcell"]');
        if (!cell) return;
        const available = cell.offsetWidth - 24; // cell padding
        const children = measure.children;
        if (!children.length || available <= 0) return;

        const badgeWidth = children[children.length - 1].offsetWidth + 4;
        let used = 0;
        let fit = 0;

        for (let i = 0; i < tags.length; i++) {
            const tagWidth = children[i].offsetWidth + (i > 0 ? 4 : 0);
            const isLast = i === tags.length - 1;
            const spaceNeeded = used + tagWidth + (isLast ? 0 : badgeWidth);
            if (spaceNeeded > available && fit > 0) break;
            used += tagWidth + (i > 0 ? 4 : 0);
            fit++;
        }

        if (fit !== fitRef.current) {
            fitRef.current = fit;
            forceUpdate(n => n + 1);
        }
    }, [tags]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const cell = container.closest('[role="gridcell"]');
        if (!cell) return;

        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(cell);
        return () => ro.disconnect();
    }, [compute]);

    if (!tags.length) return null;

    const visibleCount = fitRef.current;
    const remaining = tags.length - visibleCount;

    const content = (
        <span ref={containerRef} style={{display: 'inline-flex', alignItems: 'center', gap: 4, overflow: 'hidden'}}>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: 4, position: 'absolute', visibility: 'hidden', pointerEvents: 'none', whiteSpace: 'nowrap'}} ref={measureRef}>
                {tags.map(t => <Tag key={t} minimal small round intent="primary">{t}</Tag>)}
                <Tag minimal small round>+{tags.length}</Tag>
            </span>
            {tags.slice(0, visibleCount).map(t =>
                <Tag key={t} minimal small round intent="primary">{t}</Tag>
            )}
            {remaining > 0 && <Tag minimal small round>+{remaining}</Tag>}
        </span>
    );

    if (remaining <= 0) return content;

    return (
        <Popover
            interactionKind="hover"
            placement="right"
            hoverOpenDelay={200}
            hoverCloseDelay={100}
            minimal
            content={
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, maxWidth: 300}}>
                    {tags.map(t => <Tag key={t} minimal small round intent="primary">{t}</Tag>)}
                </div>
            }
        >
            {content}
        </Popover>
    );
};

// -------------------------------------------------
// ----------------------------------- columns model
const LocationModelColumns = [
    {
        key: 'name',
        name: 'Name',
        resizable: true,
        sortable: true,
        filterable: true,
        showDefaultFilter: true,
        formatter: (params) =>
            <Link to={'/locs/' + params.row.id}>{params.row.name}</Link>
    },
    {
        key: 'devices',
        name: 'Devices',
        width: 90,
        formatter: (params) =>
            <Link to={'/devs?loc=' + params.row.id}>
                <Tag round interactive>
                    Devices
                </Tag>
            </Link>
    },
    {
        key: 'cameras',
        name: 'Cameras',
        width: 90,
        formatter: (params) =>
            <Link to={'/recs?loc=' + params.row.id}>
                <Tag round interactive>
                    Cameras
                </Tag>
            </Link>
    },
    {
        key: 'organisation.name',
        name: 'Organisation',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => {
            const orgid = params.row?.organisation?._id ?? params.row?.organisation?.id;
            return orgid ?
                <Link to={'/orgs/' + orgid}>
                    {params.row.organisation.name}
                </Link> : ''
        }
    },
    {
        key: 'status',
        name: 'Status',
        width: 150,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'timezone',
        name: 'Timezone',
        width: 150,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'currency',
        name: 'Currency',
        width: 150,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'salesDataId',
        name: 'Sales Data ID',
        width: 150,
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'tags',
        name: 'Tags',
        width: 150,
        resizable: true,
        filterable: true,
        formatter: (params) =>
            <AutoFitTagsCell tags={(params.row.tags || []).map(formatTag)} />,
    },
    {
        key: 'createdAt',
        name: 'Created',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.createdAt}>{timeAgo(params.row.createdAt)}</span>,
    },
    {
        key: 'updatedAt',
        name: 'Updated',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.updatedAt}>{timeAgo(params.row.updatedAt)}</span>,
    },
];

const ManyLocs = ({search}) => {
    const {setLoading, setNav, setControls, setCount, loading} = useContext(DataPageContext);
    const [data, setData] = useState(null);
    const [org, setOrg] = useState(undefined);
    const [creating, setCreating] = useState(false);

    const toggleCreating = useCallback(() => {
        setCreating(prev => !prev);
    }, []);

    useEffect(() => {
        setControls([
            {name: "Create", onClick: toggleCreating, icon: "add"}
        ]);
    }, [toggleCreating]);

    useEffect(() => {
        console.log("loading locs");
        setLoading({status: true, location: "Loading locations..."});

        locations.list({
            include: "organisationDetails,salesDataId,tagDetails",
            withinOrganisations: search.get('org') || undefined,
        }).then(async locs => {
            console.log("Loaded " + locs.length + " locs");
            if (search.get('org')) {
                const fetchedOrg = await organisations.get(search.get('org'));
                setOrg(fetchedOrg);
                setData(locs);
                setLoading({status: false, location: fetchedOrg.name + " locations"});
                setNav({
                    left: [{
                        name: fetchedOrg.name,
                        path: "/orgs/" + fetchedOrg.id,
                        icon: "office",
                        subpaths: [
                            {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + fetchedOrg.id},
                            {name: "Devices", icon: "desktop", path: '/devs?org=' + fetchedOrg.id},
                            {name: "Cameras", icon: "camera", path: '/recs?org=' + fetchedOrg.id},
                            {name: "Users", icon: "people", path: '/orgs/' + fetchedOrg.id + '/users'},
                        ]
                    }],
                });
            } else {
                setData(locs);
                setLoading({status: false, location: "All locations"});
            }
        }).catch(error => {
            console.error(error);
            setLoading({status: false, location: "Failed to load locations..."});
            setData(error);
        });
    }, [search]);

    return (
        <Fragment>
            <CreateLocation
                isOpen={creating}
                preSelectedOrg={org}
                onClose={() => {
                    toggleCreating();
                }}
                onSave={(info, saveOrg) => {
                    console.log("Creating location: ", info);
                    const currentNavContext = loading.location;
                    setLoading({status: true, location: `Creating location ${info.name} for ${saveOrg.name}...`});
                    locations.create(info).then((ret) => {
                        ret.organisation = saveOrg;
                        console.log("Created location: ", ret);
                        successToast({message: `Created location ${ret.name} for ${saveOrg.name}`});
                        if (org && org.id === saveOrg.id)
                            setData(prev => [...prev, ret]);
                    }).catch((err) => {
                        console.log(err);
                        errorToast({message: "Failed to save " + err.message, timeout: 5000});
                    }).finally(() => {
                        toggleCreating();
                        setLoading({status: false, location: currentNavContext});
                    });
                }}
            />
            <SearchableDataGrid
                columns={LocationModelColumns}
                data={data ?? []}
                defaultSort='name'
                columnFilterFns={{
                    tags: (row, key, term) =>
                        (row.tags || []).some(t => formatTag(t).toLowerCase().includes(term)),
                }}
                onCount={(filtered, total) => setCount({filtered, total})}
            />
        </Fragment>
    );
}

export {ManyLocs};